import {
  addMinutes,
  differenceInMilliseconds,
  format,
  fromUnixTime,
  getUnixTime,
  isToday,
  isYesterday,
  subDays,
} from "date-fns";
import { useFeatureFlagEnabled } from "posthog-js/react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useInfiniteQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import {
  ConversationMessagesDocument,
  ConversationMessagesQuery,
  useCreateMessageMutation,
  useGetOrCreateConversationQuery,
  useMarkMessageAsReadMutation,
} from "../graphql/generated";
import { queryKeys } from "../graphql/queryKeys";
import useAnalytics from "../hooks/useAnalytics";
import useGqlClient from "../hooks/useGqlClient";
import { authSelectors } from "../store/auth/selector";
import styled, { css } from "../styles";
import { demoBookingConfig } from "../utils/demoBookingConfig";
import Avatar from "./Avatar";
import { Flex } from "./Flex";
import { SendMessageIcon } from "./icons/chat/SendMessageIcon";
import Loading from "./Loading";
import { Text } from "./Text";
import { View } from "./View";

const UPLOAD_BUTTON_HEIGHT = 32;
const AVATAR_SIZE = 30;
const MESSAGE_GROUP_TIME_LIMIT = 5 * 60 * 1000; // 5 minutes in milliseconds

const Chat = ({
  brandId,
  creatorId,
  bookingId,
  contentBriefBookingId,
}: {
  brandId: string;
  creatorId: string;
  bookingId?: string;
  contentBriefBookingId?: string;
}) => {
  const [message, setMessage] = useState("");
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const messagesContainerRef = React.useRef<HTMLDivElement>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const client = useGqlClient();
  const { track } = useAnalytics();
  const account = useSelector(authSelectors.account);
  const [textAreaFocused, setTextAreaFocused] = useState(false);
  const firstMessage = React.useMemo(() => subDays(new Date(), 1), []);
  const queryClient = useQueryClient();

  // Observer ref for infinite scrolling
  const observer = useRef<IntersectionObserver | null>(null);

  // Add the mark message as read mutation
  const { mutate: markMessageAsRead } = useMarkMessageAsReadMutation(client);

  // Track messages already marked as read to prevent infinite requests
  const [markedAsReadIds, setMarkedAsReadIds] = useState<Set<string>>(
    new Set()
  );

  const demoModeEnabled = useFeatureFlagEnabled("demo-mode");

  // Check if we're in demo mode for this brand
  let isDemoMode =
    demoModeEnabled && demoBookingConfig.some((brand) => brand.id === brandId);

  // Demo messages definition (unchanged)
  const MESSAGES = [
    {
      id: "2",
      message: "Hey 👋",
      createdAt: getUnixTime(firstMessage),
      type: "text",
      isCreator: true,
      isRead: true,
      account: {
        id: "2",
        firstName: "John",
        lastName: "Doe",
      },
    },
    {
      id: "1",
      message: "Hello, how are you?",
      createdAt: getUnixTime(addMinutes(firstMessage, 24)),
      type: "text",
      isCreator: false,
      isRead: true,
      account: {
        id: account?.id,
        firstName: account?.firstName,
        lastName: account?.lastName,
      },
    },
    {
      id: "2",
      message: "I'm good, thank you!",
      createdAt: getUnixTime(addMinutes(firstMessage, 32)),
      type: "text",
      isCreator: true,
      isRead: true,
      account: {
        id: "2",
        firstName: "John",
        lastName: "Doe",
      },
    },
    {
      id: "3",
      message: "",
      isCreator: true,
      isRead: true,
      createdAt: getUnixTime(addMinutes(firstMessage, 67)),
      type: "attachment",
      attachmentUrl:
        "https://wenibble-images.s3.eu-central-1.amazonaws.com/posts/cre_2XLJ1BKLn4J2WVYO4Ehdy4NMoRx/18049245697929863.mp4",
      thumbnailUrl:
        "https://wenibble-images.s3.eu-central-1.amazonaws.com/post_thumbnails/cre_2XLJ1BKLn4J2WVYO4Ehdy4NMoRx/18049245697929863.jpg",
      account: {
        id: "2",
        firstName: "John",
        lastName: "Doe",
      },
    },
    {
      id: "4",
      isCreator: true,
      isRead: true,
      message: `*Caption:*\n\nNew seafood afternoon tea in London 🦞☕️ ⁣
⁣
Smith's is a part of a 4-storey restaurant and Grade II-listed building beside London's famous Smithfield Market offers prime cuts of meat and locally sourced seasonal produce including day boat fish. ⁣
⁣
This iconic restaurant brings the freshest seafood to the heart of the city. Set in a chic, industrial setting, the afternoon tea features an array of delicately prepared seafood options.⁣
⁣
The menu typically includes treats like oysters, prawns, clams and stuffed crab, all prepared with high-quality, sustainably sourced ingredients.⁣
⁣
To complete the experience, diners can pair their meal with a glass of champagne or opt for a selection of fine teas, ensuring a luxurious and refreshing afternoon. ⁣
⁣
£49pp (including champagne) ⁣
⁣
The bustling Smiths Restaurant at⁣
'Smiths' of Smithfield is a 4-minute walk from Farringdon Tube Station.⁣
⁣
📍 Smiths, 67-77 Charterhouse St, London EC1M 6HJ

#londyn #londonblogger #londontravel #afternoontea #london4all #londonlove #londonafternoontea #travelblogger #londonrooftops #cityoflondon"`,
      createdAt: getUnixTime(addMinutes(firstMessage, 98)),
      type: "text",
      account: {
        id: "2",
        firstName: "John",
        lastName: "Doe",
      },
    },
    {
      id: "2",
      isCreator: true,
      isRead: true,
      message: "How's that ☝️",
      createdAt: getUnixTime(addMinutes(firstMessage, 120)),
      type: "text",
      account: {
        id: "2",
        firstName: "John",
        lastName: "Doe",
      },
    },
  ] as ConversationMessagesQuery["conversationMessages"]["messages"] &
    {
      type: "text" | "attachment";
      attachmentUrl?: string;
      thumbnailUrl?: string;
    }[];

  // Create a sorted copy of MESSAGES instead of mutating the original array
  const demoMessages = [...MESSAGES].sort((a, b) => b.createdAt - a.createdAt);

  const { data, isLoading } = useGetOrCreateConversationQuery(
    client,
    {
      brandId,
      creatorId,
      bookingId: bookingId ?? undefined,
      contentBriefBookingId: contentBriefBookingId ?? undefined,
    },
    {
      refetchOnWindowFocus: true,
    }
  );

  const { mutate: createMessage } = useCreateMessageMutation(client);

  const conversationId = data?.getOrCreateConversation?.conversation?.id;

  // Track chat opened when conversation is loaded
  useEffect(() => {
    if (conversationId) {
      track("Chat opened", {
        conversationId,
        brandId,
        creatorId,
        bookingId: bookingId || undefined,
        contentBriefBookingId: contentBriefBookingId || undefined,
        context: bookingId
          ? "booking"
          : contentBriefBookingId
          ? "content_brief"
          : "general",
      });
    }
  }, [
    conversationId,
    track,
    brandId,
    creatorId,
    bookingId,
    contentBriefBookingId,
  ]);

  const fetchMessages = useCallback(
    async ({ pageParam = "" }) => {
      return client.request<ConversationMessagesQuery>(
        ConversationMessagesDocument,
        {
          token: pageParam,
          conversationID: conversationId || "",
          count: 50,
        }
      );
    },
    [client, conversationId]
  );

  const {
    data: messagesData,
    isLoading: messagesLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchMessages,
  } = useInfiniteQuery(
    ["conversationMessages", conversationId],
    fetchMessages,
    {
      enabled: !!conversationId && !isDemoMode,
      getNextPageParam: (lastPage) =>
        lastPage.conversationMessages.cursor || undefined,
      refetchInterval: 5000,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: true,
    }
  );

  // Get messages from query data or use demo messages in demo mode
  const messages = useMemo(() => {
    if (isDemoMode) {
      return demoMessages;
    }
    return (
      messagesData?.pages.flatMap(
        (page) => page.conversationMessages.messages
      ) || []
    );
  }, [isDemoMode, messagesData?.pages, demoMessages]);

  // Mark messages as read when they load (only once per message)
  useEffect(() => {
    if (!account?.id || !conversationId || isDemoMode || !messages?.length)
      return;

    // Find the most recent unread message from other users that we haven't already marked
    const unreadMessages = messages.filter(
      (msg) => msg.account?.id !== account?.id && !markedAsReadIds.has(msg.id)
    );

    if (unreadMessages.length > 0) {
      // Get the most recent unread message
      const mostRecentUnread = unreadMessages.sort(
        (a, b) => b.createdAt - a.createdAt
      )[0];

      // Mark as being processed to prevent duplicate requests
      setMarkedAsReadIds((prev) => new Set([...prev, mostRecentUnread.id]));

      markMessageAsRead(
        { messageId: mostRecentUnread.id },
        {
          onSuccess: () => {
            // Track message read
            track("Message read", {
              messageId: mostRecentUnread.id,
              conversationId,
              brandId,
              creatorId,
              bookingId: bookingId || undefined,
              contentBriefBookingId: contentBriefBookingId || undefined,
              context: bookingId
                ? "booking"
                : contentBriefBookingId
                ? "content_brief"
                : "general",
            });

            // Refetch all unread indicators
            queryClient.invalidateQueries([queryKeys.brandNotificationCount], {
              exact: false,
            });
            queryClient.invalidateQueries([queryKeys.brandContentBrief], {
              exact: false,
            });
            queryClient.invalidateQueries([queryKeys.brandBriefs], {
              exact: false,
            });
            queryClient.invalidateQueries([queryKeys.getBooking], {
              exact: false,
            });
            queryClient.invalidateQueries([queryKeys.brandBookings], {
              exact: false,
            });
          },
          onError: (error) => {
            // Remove from marked set on error so it can be retried
            setMarkedAsReadIds((prev) => {
              const newSet = new Set(prev);
              newSet.delete(mostRecentUnread.id);
              return newSet;
            });
            console.error(
              `Failed to mark message ${mostRecentUnread.id} as read:`,
              error
            );
          },
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- markMessageAsRead, queryClient, and track are stable refs from hooks/contexts
  }, [
    messages,
    account?.id,
    conversationId,
    isDemoMode,
    markedAsReadIds,
    bookingId,
    brandId,
    contentBriefBookingId,
    creatorId,
  ]);

  const handleSendMessage = () => {
    if (isDemoMode) {
      // In demo mode, just clear the message input
      setMessage("");
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
      return;
    }

    if (!data?.getOrCreateConversation?.conversation?.id) {
      return;
    }

    if (message.trim()) {
      setMessage("");
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }

      createMessage(
        {
          conversationId: data.getOrCreateConversation.conversation.id,
          content: message,
        },
        {
          onSuccess: () => {
            // Track message sent
            track("Message sent", {
              conversationId: data.getOrCreateConversation?.conversation?.id,
              brandId,
              creatorId,
              messageLength: message.length,
              bookingId: bookingId || undefined,
              contentBriefBookingId: contentBriefBookingId || undefined,
              context: bookingId
                ? "booking"
                : contentBriefBookingId
                ? "content_brief"
                : "general",
            });

            refetchMessages();
          },
        }
      );
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    adjustTextareaHeight();
  };

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto"; // Reset height to recalculate
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  // Format date for headers (Today, Yesterday, or date)
  const formatDateHeader = (date: Date) => {
    if (isToday(date)) {
      return "Today";
    } else if (isYesterday(date)) {
      return "Yesterday";
    } else {
      return format(date, "MMMM d, yyyy");
    }
  };

  // Group messages by date
  const groupMessagesByDate = () => {
    if (!messages || messages.length === 0) return [];

    const groups: {
      [
        key: string
      ]: ConversationMessagesQuery["conversationMessages"]["messages"];
    } = {};

    // Process messages in the order they come from the backend (newest first)
    messages.forEach(
      (
        msg: ConversationMessagesQuery["conversationMessages"]["messages"][number]
      ) => {
        const messageDate = fromUnixTime(msg.createdAt);
        const dateKey = format(messageDate, "yyyy-MM-dd");

        if (!groups[dateKey]) {
          groups[dateKey] = [];
        }

        groups[dateKey].push(msg);
      }
    );

    // Convert to array of objects with date and messages
    const result = Object.entries(groups).map(([dateKey, messages]) => {
      return {
        dateKey,
        dateHeader: formatDateHeader(new Date(dateKey)),
        // Each day's messages keep newest-first order from backend
        messages,
      };
    });

    // Sort date groups with newest dates first for column-reverse layout
    result.sort(
      (a, b) => new Date(b.dateKey).getTime() - new Date(a.dateKey).getTime()
    );

    return result;
  };

  // For infinite scroll to fetch older messages when scrolling up
  const oldestMessageRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isLoading || isFetchingNextPage) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });

      if (node) observer.current.observe(node);
    },
    [isLoading, hasNextPage, fetchNextPage, isFetchingNextPage]
  );

  // Initial height adjustment for textarea
  useEffect(() => {
    adjustTextareaHeight();
  }, []);

  // Scroll to newest messages on initial load and when new messages are sent
  useEffect(() => {
    if (messagesEndRef.current && !isFetchingNextPage) {
      messagesEndRef.current.scrollIntoView({ behavior: "auto" });
    }
  }, [messages?.length, isFetchingNextPage]);

  // Keep scroll position stable when loading older messages
  const prevMessagesLength = useRef(messages?.length || 0);
  useEffect(() => {
    // If we've loaded more (older) messages, maintain scroll position
    if (isFetchingNextPage && messages?.length > prevMessagesLength.current) {
      // With column-reverse, browser automatically maintains scroll position
      // when new content is added at the top (oldest messages)
    }
    prevMessagesLength.current = messages?.length || 0;
  }, [messages?.length, isFetchingNextPage]);

  if (!brandId || !creatorId) {
    return null;
  }

  if (!data || isLoading || messagesLoading) {
    return null;
  }

  return (
    <ChatContainer>
      <MessagesContainer ref={messagesContainerRef}>
        {/* This invisible div at the end allows us to scroll to the most recent messages */}
        <div ref={messagesEndRef} />

        {messages && messages.length > 0 ? (
          groupMessagesByDate().map((group, groupIndex) => (
            <GroupContainer key={group.dateKey}>
              <DateHeader>
                <Text
                  size="xs"
                  colorPreset="secondary"
                  weight="semi"
                  align="center"
                  style={{ userSelect: "none" }}
                >
                  {group.dateHeader}
                </Text>
              </DateHeader>

              <MessagesList>
                {group.messages.map(
                  (
                    msg: ConversationMessagesQuery["conversationMessages"]["messages"][number],
                    index: number
                  ) => {
                    const isCurrentUser = msg.account?.id === account?.id;
                    const isBrandMessage = !msg.isCreator; // Any brand team member
                    const isCreator = msg.isCreator;

                    // Show avatar on the FIRST message from each sender in their group
                    // (which appears at the bottom visually due to column-reverse)
                    // Group messages from same sender within 5 minutes
                    const isFirstInSenderGroup =
                      isCreator &&
                      (index === 0 ||
                        group.messages[index - 1]?.account?.id !==
                          msg.account?.id ||
                        differenceInMilliseconds(
                          fromUnixTime(group.messages[index - 1]?.createdAt),
                          fromUnixTime(msg.createdAt)
                        ) > MESSAGE_GROUP_TIME_LIMIT);

                    // Show name on the LAST message from each sender in their group
                    // (which appears at the top visually due to column-reverse)
                    // Group messages from same sender within 5 minutes
                    const isLastInSenderGroup =
                      index === group.messages.length - 1 ||
                      group.messages[index + 1]?.account?.id !==
                        msg.account?.id ||
                      differenceInMilliseconds(
                        fromUnixTime(msg.createdAt),
                        fromUnixTime(group.messages[index + 1]?.createdAt)
                      ) > MESSAGE_GROUP_TIME_LIMIT;

                    // Attach the observer ref to the oldest message
                    // With our sorting and column-reverse, that's the first message in the last group
                    const isOldestVisibleMessage =
                      groupIndex === groupMessagesByDate().length - 1 &&
                      index === group.messages.length - 1;

                    // Check if this message should show the "Read by creator" indicator
                    const shouldShowReadByIndicator =
                      msg.id ===
                      data?.getOrCreateConversation?.conversation
                        ?.lastMessageReadByOtherParty;

                    // Simple ref for oldest message (infinite scroll)
                    const setMessageRef = isOldestVisibleMessage
                      ? oldestMessageRef
                      : null;

                    return (
                      <MessageWrap
                        key={msg.id}
                        isSender={isBrandMessage}
                        ref={setMessageRef}
                      >
                        <MessageRow>
                          {isCreator && (
                            <AvatarWrap>
                              {isFirstInSenderGroup ? (
                                <Avatar
                                  size={AVATAR_SIZE}
                                  url={
                                    data.getOrCreateConversation?.conversation
                                      ?.creator?.preferredProfile?.avatar
                                  }
                                  borderSize={0}
                                  withShadow={false}
                                />
                              ) : (
                                <View />
                              )}
                            </AvatarWrap>
                          )}
                          <div style={{ width: "100%" }}>
                            <Flex
                              justify={
                                isBrandMessage ? "flex-end" : "flex-start"
                              }
                            >
                              {/* Show name and timestamp only on first message in group (last chronologically) */}
                              {isLastInSenderGroup && (
                                <>
                                  {!isBrandMessage && (
                                    <>
                                      <Text
                                        size="xxs"
                                        colorPreset="secondary"
                                        margin="0 xs xxs 0"
                                        align="left"
                                      >
                                        {msg.account?.firstName || "Creator"}
                                        {" · "}
                                      </Text>
                                      <Text
                                        size="xxs"
                                        colorPreset="secondary"
                                        margin="0 0 xxs 0"
                                        align="left"
                                      >
                                        {format(
                                          fromUnixTime(msg.createdAt),
                                          "h:mm a"
                                        )}
                                      </Text>
                                    </>
                                  )}

                                  {isBrandMessage && (
                                    <>
                                      {!isCurrentUser && (
                                        <Text
                                          size="xxs"
                                          colorPreset="secondary"
                                          margin="0 xs xxs 0"
                                          align="right"
                                        >
                                          {msg.account?.firstName ||
                                            "Team member"}
                                          {" · "}
                                        </Text>
                                      )}
                                      <Text
                                        size="xxs"
                                        colorPreset="secondary"
                                        margin="0 0 xxs 0"
                                        align="right"
                                      >
                                        {format(
                                          fromUnixTime(msg.createdAt),
                                          "h:mm a"
                                        )}
                                      </Text>
                                    </>
                                  )}
                                </>
                              )}
                            </Flex>
                            <div>
                              <MessageBubble
                                isSender={isBrandMessage}
                                isCurrentUser={isCurrentUser}
                              >
                                <Text
                                  size="s"
                                  margin="0 0 0"
                                  style={{ whiteSpace: "pre-wrap" }}
                                >
                                  {msg.message}
                                </Text>
                              </MessageBubble>
                              {/* Show read receipt only for brand's most recent message read by creator */}
                              {shouldShowReadByIndicator && (
                                <Text
                                  size="xxs"
                                  colorPreset="secondary"
                                  align="right"
                                  margin="xs 0 0"
                                >
                                  Read by{" "}
                                  {data?.getOrCreateConversation?.conversation
                                    ?.creator?.accounts[0]?.firstName ||
                                    "Creator"}
                                </Text>
                              )}
                            </div>
                          </div>
                        </MessageRow>
                      </MessageWrap>
                    );
                  }
                )}
              </MessagesList>
            </GroupContainer>
          ))
        ) : (
          <View style={{ flex: 1 }}>
            <DateHeader>
              <Text
                size="xs"
                colorPreset="secondary"
                weight="semi"
                align="center"
                style={{ userSelect: "none" }}
              >
                Today
              </Text>
            </DateHeader>
            <View
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "90%",
                maxWidth: "70%",
                margin: "0 auto",
                flex: 1,
              }}
            >
              <Text
                size="s"
                margin="0 0 0"
                align="center"
                colorPreset="secondary"
              >
                No previous messages
              </Text>
            </View>
          </View>
        )}
        {/* Loading indicator for fetching older messages - at the top when using column-reverse */}
        {isFetchingNextPage && (
          <Flex justify="center" align="center">
            <Loading />
          </Flex>
        )}
      </MessagesContainer>
      <ChatFooter>
        <ChatInputWrapper isFocused={textAreaFocused}>
          <ChatInput
            ref={textareaRef}
            placeholder="Write a message..."
            value={message}
            onChange={handleMessageChange}
            onKeyDown={handleKeyPress}
            rows={4}
            onFocus={() => setTextAreaFocused(true)}
            onBlur={() => setTextAreaFocused(false)}
            autoFocus={true}
          />
          <MessageSendButton onClick={handleSendMessage}>
            <SendMessageIcon
              width={UPLOAD_BUTTON_HEIGHT}
              height={UPLOAD_BUTTON_HEIGHT}
              colorPreset="link"
            />
          </MessageSendButton>
        </ChatInputWrapper>
      </ChatFooter>
    </ChatContainer>
  );
};

export default Chat;

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
  box-sizing: border-box;
`;

const DateHeader = styled.div`
  display: flex;
  justify-content: center;
  margin: ${(p) => p.theme.spacing.l} 0;
  position: relative;

  &:after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    height: 1px;
    background-color: ${(p) => p.theme.color.typography.secondary}20;
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
    background-color: ${(p) => p.theme.color.card.background};
    padding: 0 ${(p) => p.theme.spacing.m};
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column-reverse;
  gap: ${(p) => p.theme.spacing.s};
  padding: ${(p) => p.theme.spacing.l} ${(p) => p.theme.spacing.l} 0;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${(p) => p.theme.color.typography.secondary}30;
    border-radius: 10px;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${(p) => p.theme.color.typography.secondary}50;
    opacity: 1;
  }

  /* Show scrollbar when hovering or actively scrolling */
  &:hover::-webkit-scrollbar-thumb,
  &:active::-webkit-scrollbar-thumb {
    opacity: 1;
  }

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;

  &:hover,
  &:active {
    scrollbar-color: ${(p) => p.theme.color.typography.secondary}30 transparent;
  }
`;

const MessageBubble = styled.div<{
  isSender: boolean;
  isCurrentUser?: boolean;
}>`
  padding: ${(p) => p.theme.spacing.s} ${(p) => p.theme.spacing.m};
  border-radius: ${(p) => p.theme.misc.borderRadiusSmall};
  background-color: ${(p) => {
    if (p.isSender) {
      // Brand messages: current user gets primary color, teammates get secondary
      return p.isCurrentUser
        ? `${p.theme.color.primary}25`
        : `${p.theme.color.typography.secondary}18`;
    } else {
      // Creator messages: always secondary
      return `${p.theme.color.typography.secondary}18`;
    }
  }};
  position: relative;
  word-break: break-word;
  overflow-wrap: break-word;

  ${(p) =>
    p.isSender
      ? css`
          border-bottom-right-radius: 0;
        `
      : css`
          border-bottom-left-radius: 0;
        `}
`;

const MessageWrap = styled.div<{ isSender: boolean }>`
  max-width: 80%;
  border-radius: ${(p) => p.theme.misc.borderRadiusSmall};
  align-self: ${(p) => (p.isSender ? "flex-end" : "flex-start")};
  margin-bottom: ${(p) => p.theme.spacing.m};
`;

const ChatFooter = styled.div`
  padding: 0 ${(p) => p.theme.spacing.m} ${(p) => p.theme.spacing.m}
    ${(p) => p.theme.spacing.m};
`;

const ChatInputWrapper = styled.div<{ isFocused: boolean }>`
  position: relative;
  width: 100%;
  border-radius: ${(p) => p.theme.misc.borderRadiusLarge};
  box-sizing: border-box;
  padding: ${(p) => p.theme.spacing.m};
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spacing.m};
  justify-content: space-between;
  border: 1px solid ${(p) => p.theme.color.typography.secondary}50;

  ${(p) =>
    p.isFocused &&
    css`
      border-color: ${(p) => p.theme.color.primary};
      box-shadow: 0px 0px 0px 2px ${(p) => p.theme.color.input.hover};
      outline: none;
    `}
`;

const ChatInput = styled.textarea`
  flex: 1;
  border: none;
  outline: none;
  resize: none;
  min-height: 24px;
  max-height: 420px;
  overflow-y: auto;
  width: 100%;
  box-sizing: border-box;
  font-size: ${(p) => p.theme.typography.size.m};
  font-weight: ${(p) => p.theme.typography.weight.normal};
  color: ${(p) => p.theme.color.typography.text};
  line-height: 1.4;
  padding: 0;
  background: transparent;

  /* Custom scrollbar styling - smaller and subtle */
  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${(p) => p.theme.color.typography.secondary}30;
    border-radius: 10px;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${(p) => p.theme.color.typography.secondary}50;
    opacity: 1;
  }

  /* Show scrollbar when hovering or actively scrolling */
  &:hover::-webkit-scrollbar-thumb,
  &:active::-webkit-scrollbar-thumb {
    opacity: 1;
  }

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;

  &:hover,
  &:active {
    scrollbar-color: ${(p) => p.theme.color.typography.secondary}30 transparent;
  }
`;

const MessageSendButton = styled.div`
  margin-top: auto;
  border: none;
  outline: none;
  cursor: pointer;
  display: flex;
  align-items: flex-end;
  transition: filter 0.15s ease;

  &:hover {
    filter: brightness(0.9);
  }
`;

const MessageRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${(p) => p.theme.spacing.xs};
  width: 100%;
`;

const AvatarWrap = styled.div`
  width: ${AVATAR_SIZE}px;
  height: ${AVATAR_SIZE}px;
  min-width: ${AVATAR_SIZE}px;
  margin-bottom: ${(p) => p.theme.spacing.xs};
  display: flex;
  align-items: flex-end;
`;

const GroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: ${(p) => p.theme.spacing.xl};
`;

const MessagesList = styled.div`
  display: flex;
  flex-direction: column-reverse;
  width: 100%;
`;
